// ============================================================
// Anzeigeformate. Die Zeichenketten der Oberfläche stehen in texte.ts (TX);
// hier stehen die Formatierer, die TX benutzt (SPEC §5.9).
//
// Zwei Regeln, die hier durchgehalten werden:
//
// 1. `b` IST AUSSCHLIESSEND. Ein Eintrag von `a = 24321` (Okt 2026) bis
//    `b = 24324` endet MIT Dezember, nicht mit Januar. Für jede Anzeige wird
//    deshalb mit `Math.ceil(b) - 1` gerechnet (SPEC §5.9, T37).
// 2. Deutsches Dezimalkomma in allem, was ein Mensch liest („2,5 Monate"),
//    aber niemals im Datenmodell.
//
// Die Monatsnamen kommen aus dem Kern (`MON`) — sie stehen auch auf dem
// Blatt, und zwei Listen liefen auseinander.
// ============================================================
import { MON, parseYM } from "@kern";

/** Monatsindex → `"Okt 2026"`. */
export function monatKurz(mi: number): string {
  const m = Math.floor(mi);
  return `${MON[((m % 12) + 12) % 12]} ${Math.floor(m / 12)}`;
}

/**
 * Zeitraum als ein Satzteil. `b` ist ausschließend, also ist der letzte
 * gezeigte Monat `Math.ceil(b) - 1`.
 *
 *   ein Monat          → `"Okt 2026"`
 *   gleiches Jahr      → `"Okt–Dez 2026"`
 *   über den Jahreswechsel → `"Nov 2026 – Jan 2027"`
 */
export function zeitraumText(a: number, b: number): string {
  const von = Math.floor(a);
  const bis = Math.max(von, Math.ceil(b) - 1);
  const jahrVon = Math.floor(von / 12);
  const jahrBis = Math.floor(bis / 12);
  if (von === bis) return monatKurz(von);
  if (jahrVon === jahrBis) {
    return `${MON[((von % 12) + 12) % 12]}–${MON[((bis % 12) + 12) % 12]} ${jahrVon}`;
  }
  return `${monatKurz(von)} – ${monatKurz(bis)}`;
}

/**
 * Dasselbe für die ISO-Tagesfelder einer Listenzeile
 * (`zeitraum_von`/`zeitraum_bis` sind `"YYYY-MM-DD"`, nicht `"YYYY-MM"`,
 * SPEC §5.4.1). Fehlt eines der beiden, kommt eine leere Zeichenkette —
 * eine Listenzeile ohne Zeitraum zeigt gar nichts an, nicht „—".
 */
export function zeitraumTextIso(von: string | null, bis: string | null): string {
  if (!von || !bis) return "";
  const a = parseYM(von.slice(0, 7));
  const b = parseYM(bis.slice(0, 7)) + 1;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return "";
  return zeitraumText(a, b);
}

/** Dauer in Monaten, auf halbe Monate gerundet: `"1 Monat"`, `"2,5 Monate"`. */
export function dauerText(n: number): string {
  const m = Math.round(n * 2) / 2;
  return m === 1 ? "1 Monat" : `${String(m).replace(".", ",")} Monate`;
}

/**
 * Zeitstempel als Abstand zu jetzt: `"vor 2 Std."`, `"gestern"`,
 * `"vor 3 Tagen"`, ab 14 Tagen das Datum `"12.08.2026"`.
 * Ein unlesbarer Zeitstempel ergibt `""` und nicht „Invalid Date".
 */
export function relativZeit(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 45) return "gerade eben";
  if (s < 90) return "vor einer Minute";
  const min = Math.round(s / 60);
  if (min < 60) return `vor ${min} Min.`;
  const std = Math.round(min / 60);
  if (std < 24) return `vor ${std} Std.`;
  const tage = Math.round(std / 24);
  if (tage === 1) return "gestern";
  if (tage < 14) return `vor ${tage} Tagen`;
  const d = new Date(t);
  const zwei = (n: number) => String(n).padStart(2, "0");
  return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Restsekunden als `"0:41"` — für „Code erneut senden" (SPEC §5.9). */
export function sekundenKurz(s: number): string {
  const ganz = Math.max(0, Math.ceil(s));
  return `${Math.floor(ganz / 60)}:${String(ganz % 60).padStart(2, "0")}`;
}

/**
 * Monate zur Auswahl in den Feldern der linken Leiste (`Von`, `Bis`,
 * `Ab hier zu Quartalen zusammenfassen`): der Zeitraum des Plans plus
 * 24 Monate an beiden Enden, damit man ihn in beide Richtungen ausweiten
 * kann, ohne eine Zahl zu tippen (SPEC §5.6.5).
 * `mitKeine` stellt `["", "— keine —"]` davor (Verdichtung abschalten).
 */
export function monatOptionen(
  plan: { start: string; end: string },
  mitKeine = false,
): { value: string; label: string }[] {
  const a = parseYM(plan.start) - 24;
  const b = parseYM(plan.end) + 24;
  const raus: { value: string; label: string }[] = [];
  if (mitKeine) raus.push({ value: "", label: "— keine —" });
  for (let mi = a; mi <= b; mi += 1) {
    const jahr = Math.floor(mi / 12);
    const monat = ((mi % 12) + 12) % 12;
    raus.push({ value: `${jahr}-${String(monat + 1).padStart(2, "0")}`, label: monatKurz(mi) });
  }
  return raus;
}
